import type { TransactionRow } from '../../api/types';
import { Modal } from '../ui/Modal';

type TransactionDetailModalProps = {
  transaction: TransactionRow | null;
  open: boolean;
  onClose: () => void;
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function amount(tx: TransactionRow, field: 'gross_amount' | 'onyx_fee' | 'gateway_fee' | 'net_amount'): string {
  const v = tx[field] ?? tx.amount_display;
  return `${v} ${tx.currency}`;
}

function statusBadgeClass(status: string): string {
  if (status === 'confirmed') return 'badge badge-success';
  if (status === 'failed' || status === 'rejected') return 'badge badge-blocked';
  return 'badge badge-pending';
}

export function TransactionDetailModal({ transaction, open, onClose }: TransactionDetailModalProps) {
  if (!transaction) return null;
  const isFailed = transaction.status === 'failed' || transaction.status === 'rejected';

  return (
    <Modal title="Transaction details" open={open} onClose={onClose}>
      <div className="tx-detail-meta">
        <span className={statusBadgeClass(transaction.status)}>{transaction.status}</span>
        <span className="cell-muted">{formatDate(transaction.created_at)}</span>
      </div>
      <p className="cell-mono tx-detail-signature">
        {transaction.signature}
        {transaction.signature && !transaction.signature.startsWith('mock_') ? (
          <>
            {' '}
            <a
              href={`https://solscan.io/tx/${encodeURIComponent(transaction.signature)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tx-detail-explorer"
            >
              Solscan
            </a>
          </>
        ) : null}
      </p>

      <h3 className="tx-detail-section-title">Financial breakdown</h3>
      <div className="tx-detail-grid">
        <div className="tx-detail-item">
          <span className="profile-field-label">Gross amount</span>
          <span className="tx-detail-value">{amount(transaction, 'gross_amount')}</span>
        </div>
        <div className="tx-detail-item">
          <span className="profile-field-label">Onyx fee</span>
          <span className="tx-detail-value tx-detail-fee">{amount(transaction, 'onyx_fee')}</span>
        </div>
        <div className="tx-detail-item">
          <span className="profile-field-label">Gateway fee</span>
          <span className="tx-detail-value tx-detail-fee">{amount(transaction, 'gateway_fee')}</span>
        </div>
        <div className="tx-detail-item tx-detail-item-highlight">
          <span className="profile-field-label">Net amount</span>
          <span className="tx-detail-value profile-balance">{amount(transaction, 'net_amount')}</span>
        </div>
      </div>

      <h3 className="tx-detail-section-title">Payer &amp; routing</h3>
      <div className="tx-detail-grid">
        <div className="tx-detail-item">
          <span className="profile-field-label">Customer IP</span>
          <span className="tx-detail-value cell-mono">{transaction.customer_ip || '—'}</span>
        </div>
        <div className="tx-detail-item">
          <span className="profile-field-label">Payment sub-type</span>
          <span className="tx-detail-value">{transaction.payment_sub_type || '—'}</span>
        </div>
        <div className="tx-detail-item">
          <span className="profile-field-label">Merchant</span>
          <span className="tx-detail-value">
            {transaction.merchant_name || transaction.merchant_id?.slice(0, 8) || '—'}
          </span>
        </div>
        {transaction.web_order_id ? (
          <div className="tx-detail-item">
            <span className="profile-field-label">Order ID</span>
            <span className="tx-detail-value cell-mono">{transaction.web_order_id}</span>
          </div>
        ) : null}
        {transaction.settlement_method ? (
          <div className="tx-detail-item">
            <span className="profile-field-label">Settlement</span>
            <span className="tx-detail-value">{transaction.settlement_method}</span>
          </div>
        ) : null}
      </div>

      {transaction.merchant_wallet || transaction.merchant_vault ? (
        <>
          <h3 className="tx-detail-section-title">On-chain routing</h3>
          <div className="tx-detail-grid">
            {transaction.merchant_wallet ? (
              <div className="tx-detail-item tx-detail-item-span">
                <span className="profile-field-label">Merchant wallet</span>
                <span className="tx-detail-value cell-mono">{transaction.merchant_wallet}</span>
              </div>
            ) : null}
            {transaction.merchant_vault ? (
              <div className="tx-detail-item tx-detail-item-span">
                <span className="profile-field-label">Merchant vault (ATA)</span>
                <span className="tx-detail-value cell-mono">{transaction.merchant_vault}</span>
              </div>
            ) : null}
            {transaction.on_chain_program_id ? (
              <div className="tx-detail-item tx-detail-item-span">
                <span className="profile-field-label">Program ID</span>
                <span className="tx-detail-value cell-mono">{transaction.on_chain_program_id}</span>
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {isFailed ? (
        <>
          <h3 className="tx-detail-section-title">Error log</h3>
          <div className="tx-error-panel">
            <div>
              <span className="profile-field-label">Error code</span>
              <span className="cell-mono">{transaction.error_code || '—'}</span>
            </div>
            <div>
              <span className="profile-field-label">Decline reason</span>
              <p className="tx-decline-reason">{transaction.decline_reason || 'No reason recorded'}</p>
            </div>
          </div>
        </>
      ) : null}
    </Modal>
  );
}
